/**
 * <infinite-scroll-collection>
 *
 * Progressively appends the next page of a Shopify collection into the
 * existing product grid instead of navigating to a new page.
 *
 * Markup contract (rendered by main-collection.liquid):
 *   <infinite-scroll-collection
 *     data-product-grid-id="product-grid-{{ section.id }}"
 *     data-current-page="{{ paginate.current_page }}"
 *     data-next-url="{{ paginate.next.url }}"   (omitted on the last page)
 *   >
 *     <button data-infinite-scroll-trigger>...</button>
 *     <p data-infinite-scroll-status role="status" aria-live="polite"></p>
 *   </infinite-scroll-collection>
 *
 * If a filter/sort change re-renders the [data-ajax-container] this element
 * lives in, the browser disconnects the old element and connects the new
 * one automatically, so state (current page / next url) always comes fresh
 * from the newly rendered Liquid rather than being carried over in JS.
 */
(function () {
  if (customElements.get('infinite-scroll-collection')) return;

  class InfiniteScrollCollection extends HTMLElement {
    connectedCallback() {
      this.grid = document.getElementById(this.dataset.productGridId);
      this.trigger = this.querySelector('[data-infinite-scroll-trigger]');
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.loading = false;

      if (!this.grid || !this.trigger) return;

      this.defaultLabel = this.trigger.querySelector('[data-infinite-scroll-label]')
        ? this.trigger.querySelector('[data-infinite-scroll-label]').textContent
        : this.trigger.textContent;

      this._onTriggerClick = this.loadNextPage.bind(this);
      this.trigger.addEventListener('click', this._onTriggerClick);

      this.setupObserver();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.trigger && this._onTriggerClick) {
        this.trigger.removeEventListener('click', this._onTriggerClick);
      }
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.loading && this.dataset.nextUrl) {
              this.loadNextPage();
            }
          });
        },
        { rootMargin: '600px 0px 0px 0px' }
      );
      this.observer.observe(this.trigger);
    }

    setLabel(text) {
      const labelEl = this.trigger.querySelector('[data-infinite-scroll-label]');
      if (labelEl) {
        labelEl.textContent = text;
      } else {
        this.trigger.textContent = text;
      }
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    async loadNextPage() {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl || this.loading) return;

      this.loading = true;
      this.trigger.disabled = true;
      this.trigger.setAttribute('aria-busy', 'true');
      this.setLabel('Loading more products…');

      try {
        const response = await fetch(nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('Request failed: ' + response.status);

        const html = await response.text();
        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = parsedDoc.getElementById(this.dataset.productGridId);
        const newState = parsedDoc.querySelector(
          'infinite-scroll-collection[data-product-grid-id="' + this.dataset.productGridId + '"]'
        );

        if (newGrid) {
          const addedCount = newGrid.children.length;
          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((node) => fragment.appendChild(node));
          this.grid.appendChild(fragment);
          this.announce(addedCount + ' more products loaded');

          document.dispatchEvent(
            new CustomEvent('infinite-scroll:products-loaded', {
              detail: { grid: this.grid, addedCount: addedCount },
            })
          );

          // Re-run common lazy-load libraries if present, in case the
          // theme relies on one of these for newly injected images.
          if (window.lazySizes && typeof window.lazySizes.update === 'function') {
            window.lazySizes.update();
          }
        }

        const newNextUrl = newState ? newState.dataset.nextUrl : null;
        if (newNextUrl) {
          this.dataset.nextUrl = newNextUrl;
          this.dataset.currentPage = newState.dataset.currentPage;
          this.setLabel(this.defaultLabel);
          this.trigger.disabled = false;
          this.trigger.removeAttribute('aria-busy');
        } else {
          delete this.dataset.nextUrl;
          if (this.observer) this.observer.disconnect();
          this.trigger.setAttribute('hidden', '');
          this.announce('All products loaded');
        }

        // Keep the URL bar in sync without a full navigation, so refreshes
        // and shared links land on the right page.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(window.history.state, '', nextUrl);
        }
      } catch (error) {
        this.setLabel(this.defaultLabel);
        this.trigger.disabled = false;
        this.trigger.removeAttribute('aria-busy');
        this.announce('Could not load more products. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
})();
